<template>

  <section class="padded email-setup">

    <el-row>
      <h3 class="email-setup__title">Notification e-Mails for <b>this {{rtx}}</b></h3>
    </el-row>

    <el-alert type="info"
      :title="text('emailSetup.intro.title')"
      :description="text('emailSetup.intro.body', { rtx: rtx })"
      show-icon
      :closable="false" />

    <div class="recipients-toolbar">
      <!-- Send E-mails: a switch. Element's own switch text is aria-hidden while its side is inactive,
           so the name is a real <label> (it also makes the words clickable). The value is bound with
           :value + @change rather than v-model: a switch created with a value that is not exactly
           true / false (null, or a 1 from the API) emits input(false) at once, which v-model would
           write into the request, turning an on (1) off, with no save. -->
      <div v-if="actionData.emails" class="recipients-toolbar__mail">
        <label :id="sendMailLabelId" :for="sendMailId" class="recipients-toolbar__mail-label"
          :class="{ 'is-disabled': !isTowerCo() || isViewOnly() }">Send E-mails</label>
        <el-switch :id="sendMailId" ref="sendMailSwitch" class="recipients-toolbar__switch"
          :value="!sendMailOff"
          :disabled="!isTowerCo() || isViewOnly()"
          :aria-labelledby="sendMailLabelId"
          @change="onSendMailChange" />
        <field-cue :row="actionData" path="emails.sendMail" label="Send e-mails" layout="inline" />
      </div>

      <span class="recipients-toolbar__spacer"></span>

      <el-button size="small" type="success" plain icon="fa fa-sync-alt" class="recipients-refresh"
        :circle="isMediumOrLess"
        :disabled="loading"
        :aria-label="isMediumOrLess ? text('emailSetup.refresh') : null"
        @click="reload"><span v-if="!isMediumOrLess">{{text('emailSetup.refresh')}}</span></el-button>
    </div>

    <!-- XOL-67 (B10, UX v4 section 2): an RTA whose client is a Tower Co sends nothing; a closed RTA sends nothing more. The same
         place as the off alert, which would say the wrong thing here (turning Send E-mails on changes nothing). -->
    <el-alert v-if="rtaMailEnded" class="recipients-off recipients-ended"
      type="info"
      :title="text(rtaTcClient ? 'emailSetup.tcClient.title' : 'emailSetup.terminal.title')"
      :description="rtaTcClient ? text('emailSetup.tcClient.body', { towerCo: towerCoName() }) : ''"
      show-icon
      :closable="false" />

    <el-alert v-else-if="sendMailOff" class="recipients-off"
      type="warning"
      :title="text('emailSetup.off.title', { rtx: rtx })"
      :description="text(isRta ? 'emailSetup.off.body.rta' : 'emailSetup.off.body')"
      show-icon
      :closable="false" />

    <!-- The read model: live user data (names, e-mails, profile flags, invalid addresses).
         The request's own lists win over its copy of them, so a refetch never undoes a
         pending edit. -->
    <div class="recipients-body" :style="{ minHeight: bodyMinHeight }"
      v-loading="loading" :element-loading-text="text('emailSetup.loading')">

      <div v-if="loadState === 'error'" class="recipients-error">
        <el-alert type="error"
          :title="errorTitle"
          :description="errorBody"
          show-icon
          :closable="false" />
        <el-button v-if="errorKind === 'generic'" size="small" class="recipients-error__retry" @click="reload">{{text('emailSetup.error.retry')}}</el-button>
      </div>

      <div v-else-if="columns.length > 0" class="recipients-grid" :class="{ 'is-narrow': isMediumOrLess }">

        <section v-for="col in columns" :key="col.party" class="recipients-col" :class="'is-' + col.party" :aria-labelledby="col.headId"
          :style="{ '--alert-cols': col.view.alerts.length }">

          <!-- Delta 1 (item 7): the header carries the name, the party, Send Test, the one-line
               help and the summary, so the column reads top-down: who, what they get, how many. -->
          <div class="recipients-col__head">
            <div class="recipients-col__who">
              <h4 :id="col.headId" class="recipients-col__name">{{col.orgName}}</h4>
              <el-tag size="mini" type="info" effect="light" class="recipients-col__party">{{text('emailSetup.party.' + col.party + (isRta && col.party === 'owner' ? '.rta' : ''))}}</el-tag>
            </div>

            <div class="recipients-col__test">
              <el-tooltip placement="top" :disabled="isMediumOrLess" :content="testTip(col)" :tabindex="col.view.testCount === 0 ? 0 : -1">
                <span class="recipients-col__test-wrap">
                  <el-button size="mini" type="warning" plain icon="fa fa-envelope"
                    :loading="!!testing[col.party]"
                    :disabled="!canEdit || loading || col.view.testCount === 0"
                    :aria-describedby="testHelpId(col.party)"
                    @click="sendTest(col.party)">{{text('emailSetup.test.button')}}</el-button>
                </span>
              </el-tooltip>
              <!-- Touch has no hover: on a phone the helper is plain text under the button. -->
              <small :id="testHelpId(col.party)" class="recipients-col__test-help text-muted" :class="{ 'visually-hidden': !isMediumOrLess }">{{testTip(col)}}</small>
            </div>

            <small class="recipients-col__help">{{helpText(col)}}</small>

            <!-- A Tower Co client's RTA sends no e-mail (decision 14): no "N will get approval e-mails" and no "nobody will" warning. -->
            <el-alert v-if="col.allZero && !rtaTcClient" class="recipients-col__nobody"
              type="warning"
              :title="text('emailSetup.nobody.title', { org: col.orgName, will: col.will, rtx: rtx })"
              :description="text(isRta ? 'emailSetup.nobody.body.rta' : 'emailSetup.nobody.body', { org: col.orgName })"
              show-icon
              :closable="false" />
            <p v-else-if="!rtaTcClient" class="recipients-col__summary" aria-live="polite">
              <template v-for="(part, i) in col.parts"><span v-if="i > 0" :key="'sep' + i"> · </span><span :key="part.key" :class="{ 'is-none': part.none }"><i v-if="part.none" class="fa fa-exclamation-circle text-warning" aria-hidden="true"></i> <b v-if="!part.none">{{part.lead}}</b>{{part.rest}}</span></template>
              <span v-if="col.view.problems > 0" class="recipients-col__problems"> · <b>{{col.view.problems}}</b>{{problemsRest(col.view.problems)}}</span>
            </p>
          </div>

          <!-- Users: live, from the organisation's own profiles. Those who get e-mails are listed;
               users who would but have no address sit behind a toggle. A user who gets nothing here
               is not listed at all: they are still offered by Additional's "Add a user" select. -->
          <div class="recipients-section">
            <!-- The Unsubscribed list is now edited by the bells below, so its conflict cue sits here. -->
            <h5 class="recipients-section__title">{{text('emailSetup.users.title', { n: col.listedCount })}}
              <field-cue :row="actionData" :path="listPathOf(col.party, 'unsubscribed')" :label="pathLabel(col.party, 'unsubscribed')" layout="inline" />
            </h5>

            <small v-if="col.listedCount === 0" class="text-muted">{{emptyUsersText(col)}}</small>

            <template v-else>

              <!-- Element's Input names itself from its `label` prop: a plain `aria-label` is overwritten by it. -->
              <el-input v-if="col.searchable" v-model="userQuery[col.party]" size="small" clearable class="recipients-search"
                prefix-icon="el-icon-search"
                :placeholder="text('emailSetup.users.search.placeholder')"
                :label="text('emailSetup.users.search.aria', { org: col.orgName })"
                @input="onQueryInput(col)" />
              <span v-if="col.searchable" class="visually-hidden" role="status">{{queryStatus[col.party]}}</span>

              <small v-if="col.query && col.noMatches" class="text-muted">{{text('emailSetup.users.search.none', { org: col.orgName, q: queryOf(col.party) })}}</small>

              <!-- Group 1: users who get e-mails. -->
              <div v-if="col.recvShown.length > 0" class="recipients-group">
                <div class="recipients-group__title">{{col.query ? text('emailSetup.users.receivingMatch', { m: col.recvMatches.length, n: col.view.groups.receiving.length }) : text('emailSetup.users.receiving', { n: col.view.groups.receiving.length })}}</div>

                <div v-if="!isMediumOrLess" class="recipient-grid__head" aria-hidden="true">
                  <span class="recipient-grid__name">Name</span>
                  <span v-for="a in col.view.alerts" :key="a" class="recipient-grid__alert">{{text('emailSetup.ticks.' + a)}}</span>
                  <span></span>
                </div>

                <ul class="recipient-list recipient-grid" :aria-label="text('emailSetup.users.aria', { org: col.orgName })">
                  <li v-for="u in col.recvShown" :key="'u' + u.contact_id" class="recipient-row recipient-row--user">

                    <div class="recipient-row__who">
                      <span class="recipient-row__name">{{u.name}}</span>
                      <span class="recipient-row__sep" aria-hidden="true">·</span>
                      <span class="recipient-row__email">{{u.email}}</span>

                      <div v-if="u.invalid" class="recipient-row__problem">
                        <i class="fa fa-exclamation-circle text-danger" aria-hidden="true"></i>
                        <span>{{shortReason(u.invalid)}}</span>
                        <el-button type="text" size="mini" class="recipient-row__fix-toggle"
                          :aria-expanded="isFixOpen(userFixKey(col, u)) ? 'true' : 'false'"
                          :aria-controls="fixId(userFixKey(col, u))"
                          :aria-label="text('emailSetup.invalid.fix.aria', { who: u.name })"
                          @click="toggleFix(userFixKey(col, u))">{{isFixOpen(userFixKey(col, u)) ? text('emailSetup.invalid.fixHide') : text('emailSetup.invalid.fix')}}</el-button>
                      </div>
                      <div v-if="u.invalid && isFixOpen(userFixKey(col, u))" :id="fixId(userFixKey(col, u))" class="recipient-row__fix">
                        {{invalidReason(u.invalid, u.email)}} {{text('emailSetup.invalid.hint.profile')}}
                        <a v-if="u.link" :href="u.link.href" target="_blank" rel="noopener">{{u.link.text}}</a>
                      </div>
                    </div>

                    <div class="recipient-row__cells">
                      <span v-for="a in col.view.alerts" :key="a" class="recipient-cell" :title="cellTitle(u.alerts[a].state)">
                        <span class="recipient-cell__label" aria-hidden="true">{{text('emailSetup.ticks.' + a)}}</span>
                        <span class="visually-hidden">{{text('emailSetup.ticks.' + a)}}: </span>
                        <i :class="[cellIcon(u.alerts[a].state), cellIconColour(u.alerts[a].state)]" aria-hidden="true"></i>
                        <span class="recipient-cell__state" :class="{ 'is-off': u.alerts[a].state === 'off' || u.alerts[a].state === 'unsubscribed' }">{{cellText(u.alerts[a].state)}}</span>
                        <span class="visually-hidden">, {{cellTitle(u.alerts[a].state)}}</span>
                      </span>
                    </div>

                    <!-- Delta 5 / 5b: the user stays in place. The icon is a slashed bell while every alert of
                         the column is on, and a plain bell once an alert is Off for a user who has an
                         Additional entry or an unsubscribe (u.canTurnOn). A column with two alerts opens the
                         panel from either; a column with one (Tower Co, provider) toggles it at once. -->
                    <el-button v-if="canEdit" type="text" size="mini" class="recipient-row__unsub"
                      :class="{ 'is-unsubscribed': u.canTurnOn }"
                      :title="bellTitle(u)"
                      :aria-label="bellAria(col, u)"
                      :aria-expanded="hasPanel(col) ? (isUnsubOpen(col.party, u.contact_id) ? 'true' : 'false') : null"
                      :aria-controls="hasPanel(col) ? unsubPanelId(col.party, u.contact_id) : null"
                      @click="onUnsubscribe(col, u)"><i class="fa" :class="u.canTurnOn ? 'fa-bell' : 'fa-bell-slash'" aria-hidden="true"></i></el-button>

                    <!-- Which e-mails this user gets on this request, inline (a popover inside a drawer would
                         take Esc away from it, so this is a plain panel). Checked = they get it. -->
                    <div v-if="isUnsubOpen(col.party, u.contact_id)" :id="unsubPanelId(col.party, u.contact_id)" class="unsub-panel"
                      @keydown.esc.stop="closeUnsub">
                      <p>{{text('emailSetup.unsub.prompt', { name: u.name, rtx: rtx })}}</p>
                      <el-checkbox v-for="a in unsubAlerts(col)" :key="a" ref="unsubBox" v-model="unsub[a]">{{text('emailSetup.unsub.' + a)}}</el-checkbox>
                      <div class="unsub-panel__buttons">
                        <el-button size="mini" @click="closeUnsub">{{text('emailSetup.unsub.cancel')}}</el-button>
                        <el-button size="mini" type="success" :disabled="!unsubChanged(col, u)" @click="confirmUnsub(col, u)">{{text('emailSetup.unsub.confirm')}}</el-button>
                      </div>
                    </div>

                  </li>
                </ul>

                <el-button v-if="col.recvTruncatable" type="text" size="mini" class="recipients-group__more"
                  :aria-expanded="receivingAll[col.party] ? 'true' : 'false'"
                  @click="toggleReceivingAll(col.party)">{{receivingAll[col.party] ? text('emailSetup.users.showFewer') : text('emailSetup.users.showAll', { n: col.view.groups.receiving.length })}}</el-button>

                <small v-if="col.showLegend" class="recipients-legend"><i class="fa fa-bell text-info" aria-hidden="true"></i> {{text('emailSetup.cell.legend', { rtx: rtx })}}</small>
              </div>

              <!-- Toggle A: they would get e-mails but have no address (something to fix). -->
              <div v-if="col.query ? col.noAddressMatches.length > 0 : col.view.groups.noAddress.length > 0" class="recipients-group">
                <div v-if="col.query" class="recipients-group__title">{{text('emailSetup.users.noAddressMatch', { m: col.noAddressMatches.length })}}</div>
                <el-button v-else type="text" size="mini" class="recipients-group__toggle"
                  :icon="noAddressShown[col.party] ? 'fa fa-angle-down' : 'fa fa-angle-right'"
                  :aria-expanded="noAddressShown[col.party] ? 'true' : 'false'"
                  :aria-controls="listId(col.party, 'noaddress')"
                  @click="toggleNoAddress(col.party)"><i class="fa fa-exclamation-circle text-warning recipients-group__warn" aria-hidden="true"></i>{{noAddressShown[col.party] ? text('emailSetup.users.hideNoAddress') : text('emailSetup.users.showNoAddress', { n: col.view.groups.noAddress.length })}}</el-button>

                <ul v-if="col.query || noAddressShown[col.party]" :id="listId(col.party, 'noaddress')" class="recipient-list is-compact" :aria-label="text('emailSetup.users.noAddressMatch', { m: col.noAddressMatches.length })">
                  <li v-for="u in (col.query ? col.noAddressMatches : col.view.groups.noAddress)" :key="'n' + u.contact_id" class="recipient-row recipient-row--compact">
                    <div class="recipient-row__line">
                      <span class="recipient-row__name">{{u.name}}</span>
                      <el-tag size="mini" type="danger" effect="light">{{text('emailSetup.invalid.tag.no_email')}}</el-tag>
                      <el-button type="text" size="mini" class="recipient-row__fix-toggle"
                        :aria-expanded="isFixOpen(userFixKey(col, u)) ? 'true' : 'false'"
                        :aria-controls="fixId(userFixKey(col, u))"
                        :aria-label="text('emailSetup.invalid.fix.aria', { who: u.name })"
                        @click="toggleFix(userFixKey(col, u))">{{isFixOpen(userFixKey(col, u)) ? text('emailSetup.invalid.fixHide') : text('emailSetup.invalid.fix')}}</el-button>
                    </div>
                    <div v-if="isFixOpen(userFixKey(col, u))" :id="fixId(userFixKey(col, u))" class="recipient-row__fix">
                      {{invalidReason(u.invalid, u.email)}} {{text('emailSetup.invalid.hint.profile')}}
                      <a v-if="u.link" :href="u.link.href" target="_blank" rel="noopener">{{u.link.text}}</a>
                    </div>
                  </li>
                </ul>
              </div>

            </template>
          </div>

          <!-- Changes for this request only: Additional and Unsubscribed, in their own grey box. -->
          <div class="recipients-overrides" role="group" :aria-label="text('emailSetup.overrides.title', { rtx: rtx })">
            <div class="recipients-overrides__title">{{text('emailSetup.overrides.title', { rtx: rtx })}}</div>

            <!-- Additional: an org user by id, or a typed address. -->
            <div class="recipients-section recipients-section--first">
              <h5 class="recipients-section__title">{{text('emailSetup.additional.title', { n: col.view.additional.length })}}
                <field-cue :row="actionData" :path="listPathOf(col.party, 'additional')" :label="pathLabel(col.party, 'additional')" layout="inline" />
              </h5>
              <small class="recipients-section__help text-muted">{{text('emailSetup.additional.help', { rtx: rtx })}}</small>

              <small v-if="col.view.additional.length === 0" class="text-muted">{{text('emailSetup.additional.empty')}}</small>

              <ul v-else class="recipient-list is-overrides">
                <li v-for="(e, index) in col.view.additional" :key="e.key" class="recipient-row recipient-row--override">

                  <div class="recipient-row__who">
                    <template v-if="e.typed">
                      <span class="recipient-row__name"><span v-for="(part, pi) in emailParts(e.email)" :key="pi">{{part}}<wbr></span></span>
                      <span class="recipient-row__sep" aria-hidden="true">·</span>
                      <span class="recipient-row__meta">{{text('emailSetup.entry.typed')}}</span>
                    </template>
                    <template v-else-if="e.state === 'missing'">
                      <span class="recipient-row__name"><i>{{text('emailSetup.entry.missing')}}</i></span>
                      <el-tag size="mini" type="info">{{text('emailSetup.entry.missing.tag')}}</el-tag>
                      <small class="recipient-row__hint">{{text('emailSetup.entry.missing.hint')}}</small>
                    </template>
                    <template v-else>
                      <span class="recipient-row__name">{{e.name}}</span>
                      <span class="recipient-row__sep" aria-hidden="true">·</span>
                      <span v-if="e.email" class="recipient-row__email"><span v-for="(part, pi) in emailParts(e.email)" :key="pi">{{part}}<wbr></span></span>
                      <i v-else-if="!e.invalid" class="recipient-row__none">{{text('emailSetup.user.noEmail')}}</i>
                      <el-tag v-if="e.state === 'disabled'" size="mini" type="info">{{text('emailSetup.entry.disabled.tag')}}</el-tag>
                      <small v-if="e.state === 'disabled'" class="recipient-row__hint">{{text('emailSetup.entry.disabled.hint')}}</small>
                    </template>

                    <div v-if="e.invalid" class="recipient-row__problem">
                      <i class="fa fa-exclamation-circle text-danger" aria-hidden="true"></i>
                      <span>{{shortReason(e.invalid)}}</span>
                      <el-button type="text" size="mini" class="recipient-row__fix-toggle"
                        :aria-expanded="isFixOpen(entryFixKey(col, e)) ? 'true' : 'false'"
                        :aria-controls="fixId(entryFixKey(col, e))"
                        :aria-label="text('emailSetup.invalid.fix.aria', { who: whoOf(e) })"
                        @click="toggleFix(entryFixKey(col, e))">{{isFixOpen(entryFixKey(col, e)) ? text('emailSetup.invalid.fixHide') : text('emailSetup.invalid.fix')}}</el-button>
                    </div>
                    <div v-if="e.invalid && isFixOpen(entryFixKey(col, e))" :id="fixId(entryFixKey(col, e))" class="recipient-row__fix">
                      {{invalidReason(e.invalid, e.email)}} {{text(e.typed ? 'emailSetup.invalid.hint.typed' : 'emailSetup.invalid.hint.profile')}}
                      <a v-if="e.link" :href="e.link.href" target="_blank" rel="noopener">{{e.link.text}}</a>
                    </div>
                  </div>

                  <div v-if="col.party !== 'towerco'" class="recipient-row__ticks" role="group" :aria-label="text('emailSetup.ticks.aria', { who: whoOf(e) })">
                    <el-checkbox v-for="a in col.view.alerts" :key="a"
                      :value="e.entry[a] === true"
                      :disabled="!canEdit || isLastTick(e.entry, a, col.view.alerts)"
                      :title="isLastTick(e.entry, a, col.view.alerts) ? text('emailSetup.ticks.lastTitle') : null"
                      @change="onTick(col.party, 'additional', e.key, a, $event)">{{text('emailSetup.ticks.' + a)}}</el-checkbox>
                  </div>

                  <el-button v-if="canEdit" type="text" size="mini" class="recipient-row__remove"
                    :data-focus="'rm-' + col.party"
                    :aria-label="text('emailSetup.entry.remove.aria', { who: whoOf(e) })"
                    @click="removeAdditional(col.party, e.key, index)"><i class="fa fa-times-circle" aria-hidden="true"></i></el-button>

                </li>
              </ul>

              <div v-if="canEdit" class="recipients-add">
                <!-- Element's select does not pass aria-label to its input, so a hidden <label for> names it. -->
                <label :for="addUserId(col.party)" class="visually-hidden">{{text('emailSetup.add.user.aria', { org: col.orgName })}}</label>
                <el-select v-model="userPick[col.party]" size="small" filterable clearable style="width: 100%;"
                  :id="addUserId(col.party)"
                  :data-focus="'add-' + col.party"
                  :placeholder="text('emailSetup.add.user.placeholder', { org: col.orgName })"
                  :no-match-text="text('emailSetup.add.user.noMatch')"
                  :no-data-text="text('emailSetup.add.user.noData', { org: col.orgName })"
                  @change="onPickUser(col, $event)">
                  <el-option v-for="o in col.options" :key="o.contact_id" :value="o.contact_id" :label="o.name" :disabled="!!o.why">
                    <span>{{o.name}}</span>
                    <span class="opt-email">{{o.why || o.email}}</span>
                  </el-option>
                </el-select>

                <div class="recipients-add__typed">
                  <div class="recipients-add__input" :class="{ 'is-error': !!addError[col.party] }">
                    <el-input size="small" type="email" v-model="typed[col.party]"
                      :placeholder="text('emailSetup.add.email.placeholder')"
                      :label="text('emailSetup.add.email.aria', { org: col.orgName })"
                      :aria-invalid="addError[col.party] ? 'true' : 'false'"
                      :aria-describedby="addErrorId(col.party)"
                      @keyup.enter.native="addTyped(col)"
                      @input="clearAddError(col.party)"
                      @blur="validateTypedOnBlur(col.party)" />
                  </div>
                  <el-button size="small" type="primary" plain icon="fa fa-plus"
                    :disabled="!typed[col.party] || !typed[col.party].trim()"
                    @click="addTyped(col)">{{text('emailSetup.add.email.button')}}</el-button>
                </div>
                <p v-if="addError[col.party]" :id="addErrorId(col.party)" class="recipients-add__error" role="alert">{{addError[col.party]}}</p>
              </div>
            </div>

            <!-- Unsubscribed (Delta 5): a user with a row above is unsubscribed in place, with the bell. Only
                 the entries with no such row (a deleted or disabled contact, a user with no address) are
                 listed here, so they can still be removed. No heading and no box content when there are none. -->
            <div v-if="col.orphans.length > 0" class="recipients-section">
              <h5 class="recipients-section__title" tabindex="-1" :data-focus="'head-unsub-' + col.party">{{text('emailSetup.unsubscribed.title', { n: col.orphans.length })}}</h5>
              <small class="recipients-section__help text-muted">{{text('emailSetup.unsubscribed.help', { rtx: rtx })}}</small>

              <ul class="recipient-list is-overrides">
                <li v-for="(e, index) in col.orphans" :key="e.key" class="recipient-row recipient-row--override recipient-row--unsub">

                  <div class="recipient-row__who">
                    <template v-if="e.state === 'missing'">
                      <span class="recipient-row__name"><i>{{text('emailSetup.entry.missing')}}</i></span>
                      <el-tag size="mini" type="info">{{text('emailSetup.entry.missing.tag')}}</el-tag>
                      <small class="recipient-row__hint">{{text('emailSetup.entry.missing.hint')}}</small>
                    </template>
                    <template v-else>
                      <span class="recipient-row__name">{{e.name}}</span>
                      <span class="recipient-row__sep" aria-hidden="true">·</span>
                      <span v-if="e.email" class="recipient-row__email"><span v-for="(part, pi) in emailParts(e.email)" :key="pi">{{part}}<wbr></span></span>
                      <i v-else class="recipient-row__none">{{text('emailSetup.user.noEmail')}}</i>
                      <el-tag v-if="e.state === 'disabled'" size="mini" type="info">{{text('emailSetup.entry.disabled.tag')}}</el-tag>
                    </template>
                  </div>

                  <el-button v-if="canEdit" type="text" size="mini" class="recipient-row__resub"
                    :data-focus="'rs-' + col.party"
                    :aria-label="text('emailSetup.unsubscribed.resubscribe.aria', { who: whoOf(e) })"
                    @click="resubscribe(col.party, e.key, index)">{{text('emailSetup.unsubscribed.resubscribe')}}</el-button>

                </li>
              </ul>
            </div>
          </div>

        </section>

      </div>

    </div>

  </section>

</template>


<script>

import axios from '@/lib/axiosClient'
import RequestMixins from '@/mixins/RequestMixins'
import DateMixins from '@/mixins/DateMixins'
import FieldCue from '@/components/requests/FieldCue'
import { activityText, QUEUE_FAILURE_CODES } from '@/lib/requestActivity'
import { fieldLabel } from '@/lib/requestSyncCopy'
import { isTowerCoOrg } from '@/lib/contactRoles'
import { isTerminal } from '@/lib/rtaStage'
import {
  LIST_NAMES,
  listPath,
  isValidEmail,
  recipientKey,
  additionalEntry,
  setTick,
  setUserAlert,
  applyOverride,
  diffOps,
  resolveParty
} from '@/lib/requestRecipients'

// D31 (XOL-626): "the last e-mail to this address failed" is a problem with the ADDRESS. When the
// send failed because of the queue (the job never ran, was lost, failed, or never got queued) the
// address is fine, so it is not an e-mail problem: no tag, no fix hint, not counted. The API is
// being asked to stop sending these; this is the guard for the ones that still arrive.
function withoutQueueFailures (party) {
  var clean = function (e) {
    if (e && e.invalid && e.invalid.reason === 'last_failed' && QUEUE_FAILURE_CODES.indexOf(e.invalid.error_code) !== -1) {
      return Object.assign({}, e, { invalid: null })
    }

    return e
  }

  return Object.assign({}, party, {
    users: Array.isArray(party.users) ? party.users.map(clean) : party.users,
    additional: Array.isArray(party.additional) ? party.additional.map(clean) : party.additional,
    unsubscribed: Array.isArray(party.unsubscribed) ? party.unsubscribed.map(clean) : party.unsubscribed
  })
}

// The Settings tab that lists an organisation category's contacts (hash router).
var SETTINGS_TAB = {
  'Tower Company': 'orgs-towerco',
  'Telco': 'orgs-telco',
  'Backhaul': 'orgs-backhaul'
}

export default {
  mixins: [RequestMixins, DateMixins],

  components: {
    FieldCue
  },

  // The LayerHost's registry, when this renders inside a request layer. Only used to
  // claim Esc for the open Unsubscribe panel; null outside one.
  inject: {
    drawerDirtyRegistry: {
      default: null
    }
  },

  props: {

    rowData: {
      type: Object,
      required: true
    }

  },


  data () {
    return {
      // The read model of GET /actions/{id}/recipients: live user data per party.
      model: null,
      // 'loading' (first fetch, nothing to show yet) | 'ready' | 'error'
      loadState: 'loading',
      loading: false,
      errorKind: null,
      loadSeq: 0,

      // The Add controls and their error, per party.
      typed: { towerco: '', owner: '', provider: '' },
      addError: { towerco: null, owner: null, provider: null },
      userPick: { towerco: null, owner: null, provider: null },

      // The Users list, per party (Delta 1): the toggle ("Show N users with alerts on but no
      // e-mail address"), "Show all N" of the receiving users, and the search box. All
      // collapsed / empty until asked for.
      noAddressShown: { towerco: false, owner: false, provider: false },
      receivingAll: { towerco: false, owner: false, provider: false },
      userQuery: { towerco: '', owner: '', provider: '' },
      // The sentence a screen reader hears after the query settles (role="status").
      queryStatus: { towerco: '', owner: '', provider: '' },
      queryTimers: {},

      // The open "How to fix" blocks, by row key.
      fixOpen: {},

      // The one open Unsubscribe panel: { party, contactId } and its two ticks.
      unsubFor: null,
      unsub: { upload: true, approval: true },

      testing: { towerco: false, owner: false, provider: false }
    }
  },


  computed: {

    // Not a snapshot: the request is the prop (XOL-158).
    actionData () {
      return this.rowData
    },

    rtx () {
      return this.actionData.actionType
    },

    // XOL-67: an RTA's Emails Update words its two columns and its off alert for an RTA.
    isRta () {
      return this.rtx === 'RTA'
    },

    // XOL-67 (B10): is this RTA's client a Tower Co (it is its own client, so the RTA sends no e-mail, decision 14)?
    rtaTcClient () {
      var c = this.isRta ? (this.actionData.client || this.actionData.organisation) : null

      return !!c && isTowerCoOrg(c.category)
    },

    // XOL-67 (B10): an RTA that sends no e-mail, for either reason: a Tower Co client, or a closed RTA (Closed / Rejected).
    rtaMailEnded () {
      return this.isRta && (this.rtaTcClient || isTerminal(this.actionData))
    },

    // Editing needs a non-view-only Tower Co user (the only viewer who gets here, F1).
    canEdit () {
      return this.canWriteStep() && !!this.actionData.emails
    },

    sendMailId () {
      return 'emailsetup-sendmail-' + this._uid
    },

    sendMailLabelId () {
      return 'emailsetup-sendmail-label-' + this._uid
    },

    sendMailOff () {
      return !(this.actionData.emails && this.actionData.emails.sendMail)
    },

    bodyMinHeight () {
      return (this.loadState === 'loading' && !this.model) ? '240px' : null
    },

    errorTitle () {
      if (this.errorKind === 'forbidden') {
        return this.text('emailSetup.error.forbidden', { towerCo: this.towerCoName() })
      }
      return this.errorKind === 'notFound' ? this.text('emailSetup.error.notFound') : this.text('emailSetup.error.title')
    },

    errorBody () {
      return this.errorKind === 'generic' ? this.text('emailSetup.error.body') : ''
    },

    // One view-model per party column: the read model's live users, resolved against
    // the request's own lists (they win), so a column recomputes as the user edits.
    columns () {
      var self = this

      if (!this.model || !Array.isArray(this.model.parties)) {
        return []
      }

      return this.model.parties
        .filter(function (p) { return p.party !== 'provider' || self.hasDistinctProvider() })
        .map(function (p) {
          var view = resolveParty(withoutQueueFailures(p), self.listsOf(p.party))
          var orgName = p.organisation && p.organisation.name ? p.organisation.name : self.text('emailSetup.party.' + p.party)
          var col = {
            party: p.party,
            model: p,
            view: view,
            orgName: orgName,
            headId: 'emailsetup-' + self._uid + '-' + p.party,
            will: self.sendMailOff ? 'would' : 'will'
          }

          view.users.forEach(function (u) { u.link = self.fixLink(col, u, u.is_self) })
          view.additional.forEach(function (e) { e.link = e.typed ? null : self.fixLink(col, e, false) })

          // The Users list (Delta 1). `listedCount` = the users listed or behind the toggle.
          // A user who gets nothing here is not counted or shown (with or without an address);
          // Additional's "Add a user" select still offers the ones with an address.
          // The search box appears past 15 of them, and filters both groups.
          var g = view.groups

          col.listedCount = g.receiving.length + g.noAddress.length
          col.searchable = col.listedCount > 15
          col.query = col.searchable ? String(self.userQuery[p.party] || '').trim().toLowerCase() : ''
          col.recvMatches = g.receiving.filter(function (u) { return self.userMatches(u, col.query) })
          col.noAddressMatches = g.noAddress.filter(function (u) { return self.userMatches(u, col.query) })
          col.noMatches = !!col.query && col.recvMatches.length === 0 && col.noAddressMatches.length === 0
          col.recvTruncatable = !col.query && g.receiving.length > 15
          col.recvShown = col.query
            ? col.recvMatches
            : ((col.recvTruncatable && !self.receivingAll[p.party]) ? g.receiving.slice(0, 10) : g.receiving)
          col.showLegend = col.recvShown.some(function (u) {
            return u.canTurnOn || view.alerts.some(function (a) { return u.alerts[a].state === 'added' || u.alerts[a].state === 'unsubscribed' })
          })

          // Delta 5: the Unsubscribed entries with no row in the list.
          col.orphans = view.unsubscribed.filter(function (e) { return !e.listed })

          col.parts = self.summaryParts(col)
          col.allZero = col.parts.every(function (part) { return part.none })
          col.options = self.userOptions(col)

          return col
        })
    }

  },


  methods: {

    text (key, params) {
      return activityText(key, params)
    },

    // The recipient lists are live: every open of the popup (and Refresh) re-reads them.
    // The latest request wins; an answer to an older one is dropped.
    reload () {
      var self = this
      var action = this.actionData
      var seq = this.loadSeq + 1

      this.loadSeq = seq
      this.loading = true

      if (!this.model) {
        this.loadState = 'loading'
      }

      return axios.get(this.getEndpoint('/actions/' + action.id + '/recipients')).then(function (response) {
        if (seq !== self.loadSeq) {
          return
        }

        var data = response.data

        if (!data || !Array.isArray(data.parties)) {
          self.fail('generic')
          return
        }

        self.model = data
        self.errorKind = null
        self.loadState = 'ready'
        self.loading = false
      }, function (err) {
        if (seq !== self.loadSeq) {
          return
        }

        var status = err && err.response ? err.response.status : null

        console.error('XOL-586: could not load the recipients', status, err)
        self.fail(status === 403 ? 'forbidden' : (status === 404 ? 'notFound' : 'generic'))
      })
    },

    fail (kind) {
      this.errorKind = kind
      this.loadState = 'error'
      this.loading = false
    },

    // ---- The request's own lists -----------------------------------------

    listPathOf (party, list) {
      return listPath(party, list)
    },

    pathLabel (party, list) {
      return fieldLabel('action', listPath(party, list), {
        towerCo: this.towerCoName(),
        telco: this.actionData.organisation ? this.actionData.organisation.name : 'the telco',
        provider: this.actionData.providerShortName || null
      })
    },

    // The party's lists as they are now (empty when the request has none yet).
    listsOf (party) {
      var emails = this.actionData.emails
      var holder = emails && emails[party]

      return {
        additional: (holder && Array.isArray(holder.additional)) ? holder.additional : [],
        unsubscribed: (holder && Array.isArray(holder.unsubscribed)) ? holder.unsubscribed : []
      }
    },

    // A request created before the live lists has no party object yet: give it the
    // canonical empty one before the first edit.
    ensureParty (party) {
      var emails = this.actionData.emails

      if (!emails[party] || typeof emails[party] !== 'object') {
        this.$set(emails, party, { additional: [], unsubscribed: [] })
      }

      for (var i = 0; i < LIST_NAMES.length; i++) {
        if (!Array.isArray(emails[party][LIST_NAMES[i]])) {
          this.$set(emails[party], LIST_NAMES[i], [])
        }
      }

      return emails[party]
    },

    // XOL-596: put `next` in a list IN PLACE (the request row aliases it), record the
    // ops (add / set / remove, by key) a 409 re-applies onto the server's list, and
    // mark the list changed. Saved AT ONCE (Carlos, 2026-10-18: e-mail configuration
    // has no 3 s pause), through the same per-field save, conflict handling and save
    // status as the rest of the request. `deferSave` lets commitLists save both lists
    // of a party together.
    commitList (party, list, next, deferSave) {
      var holder = this.ensureParty(party)
      var current = holder[list]
      var ops = diffOps(current, next)

      if (ops.length === 0) {
        return false
      }

      var path = listPath(party, list)
      var self = this

      current.splice.apply(current, [0, current.length].concat(next))

      ops.forEach(function (op) {
        self.recordListOp(self.actionData, path, op)
      })

      this.fieldChanged(this.actionData, path)

      if (!deferSave) {
        this.saveNow([path])
      }
      return true
    },

    // A user action can change both lists of a party (the two are kept exclusive).
    commitLists (party, next) {
      var paths = []

      if (this.commitList(party, 'additional', next.additional, true)) {
        paths.push(listPath(party, 'additional'))
      }
      if (this.commitList(party, 'unsubscribed', next.unsubscribed, true)) {
        paths.push(listPath(party, 'unsubscribed'))
      }
      this.saveNow(paths)
    },

    // Save the given `emails.*` paths now, not at the form's pause. Resolves when done;
    // a refusal is reported by the normal save notices.
    saveNow (paths) {
      if (!paths || paths.length === 0) {
        return Promise.resolve(null)
      }

      var row = this.actionData

      return this.flushFieldPaths(paths.map(function (path) { return { row: row, path: path } }))
    },

    // ---- Edits ----------------------------------------------------------------

    // A tick of an Additional / Unsubscribed entry.
    onTick (party, list, key, alert, checked) {
      if (!this.canEdit) return

      // Delta 5b: a user's Additional entry may end with every tick off (it stays until the (X)).
      this.commitList(party, list, setTick(this.listsOf(party)[list], key, alert, checked, list === 'additional'))
    },

    removeAdditional (party, key, index) {
      if (!this.canEdit) return

      this.commitList(party, 'additional', this.listsOf(party).additional.filter(function (e) {
        return recipientKey(e) !== key
      }))
      this.focusAfterRemoval('rm-' + party, 'add-' + party, index)
    },

    resubscribe (party, key, index) {
      if (!this.canEdit) return

      this.commitList(party, 'unsubscribed', this.listsOf(party).unsubscribed.filter(function (e) {
        return recipientKey(e) !== key
      }))
      this.focusAfterRemoval('rs-' + party, 'add-' + party, index)
    },

    // Add an org user who is not receiving, with the ticks they do not already have.
    onPickUser (col, contactId) {
      var party = col.party

      if (contactId === null || contactId === undefined || contactId === '') return

      var user = null
      col.view.users.forEach(function (u) { if (Number(u.contact_id) === Number(contactId)) user = u })

      this.userPick[party] = null

      if (!user || !this.canEdit) return

      var next = this.listsOf(party)

      col.view.alerts.forEach(function (a) {
        if (!user.alerts[a].receives) {
          next = applyOverride(next, user.contact_id, a, 'add')
        }
      })

      this.commitLists(party, next)
      this.focusEl('[data-focus="add-' + party + '"] input')
    },

    // The bell (Delta 5 / 5b). A column with one alert (Tower Co, provider) flips it at once: an alert the
    // user gets is turned off, one they don't is turned on (see setUserAlert for how each is done). A
    // column with two opens the panel from either icon.
    onUnsubscribe (col, user) {
      if (!this.hasPanel(col)) {
        var only = col.view.alerts[0]

        this.applyAlerts(col, user, [[only, !user.alerts[only].receives]])
        return
      }

      if (this.isUnsubOpen(col.party, user.contact_id)) {
        this.closeUnsub()
        return
      }

      var checked = {}
      var self = this

      col.view.alerts.forEach(function (a) { checked[a] = user.alerts[a].receives })

      this.unsubFor = { party: col.party, contactId: user.contact_id }
      this.unsub = Object.assign({ upload: false, approval: false }, checked)

      this.$nextTick(function () {
        var first = self.$refs.unsubBox
        var box = Array.isArray(first) ? first[0] : first
        self.focusEl(null, box ? box.$el.querySelector('input') : null)
      })
    },

    // A column that carries more than one alert asks which, in the panel.
    hasPanel (col) {
      return col.view.alerts.length > 1
    },

    // The alerts the panel offers: every alert of the column (Delta 5b), so one that is Off can be
    // turned on (added) from the bell, not only the ones they are eligible for.
    unsubAlerts (col) {
      return col.view.alerts
    },

    // Does the panel differ from what the user gets now? Save is disabled until it does.
    unsubChanged (col, user) {
      var self = this

      return this.unsubAlerts(col).some(function (a) { return !!self.unsub[a] !== user.alerts[a].receives })
    },

    // Save applies only the difference: an unchecked alert they get is turned off, a checked alert they
    // don't get is turned on.
    confirmUnsub (col, user) {
      var self = this
      var changes = this.unsubAlerts(col).filter(function (a) {
        return !!self.unsub[a] !== user.alerts[a].receives
      }).map(function (a) { return [a, !!self.unsub[a]] })

      if (!this.canEdit || changes.length === 0) return

      var open = this.unsubFor

      this.applyAlerts(col, user, changes)

      if (open) {
        this.focusEl('[aria-controls="' + this.unsubPanelId(open.party, open.contactId) + '"]')
      }
    },

    // Apply [[alert, on], ...] for one user, one alert after the other (each writes only its own tick),
    // then commit both lists. The panel closes.
    applyAlerts (col, user, changes) {
      if (!this.canEdit || changes.length === 0) return

      var next = this.listsOf(col.party)

      changes.forEach(function (c) {
        next = setUserAlert(next, user, c[0], c[1])
      })

      this.unsubFor = null
      this.commitLists(col.party, next)
    },

    isUnsubOpen (party, contactId) {
      return !!this.unsubFor && this.unsubFor.party === party && Number(this.unsubFor.contactId) === Number(contactId)
    },

    unsubPanelId (party, contactId) {
      return 'unsub-' + this._uid + '-' + party + '-' + contactId
    },

    closeUnsub () {
      var open = this.unsubFor

      this.unsubFor = null

      if (open) {
        this.focusEl('[aria-controls="' + this.unsubPanelId(open.party, open.contactId) + '"]')
      }
    },

    // Esc claim (the FieldCue pattern): while a panel is open it takes the key first.
    claimEsc () {
      if (!this.unsubFor) {
        return false
      }
      this.closeUnsub()
      return true
    },

    // Element's switch renders `aria-checked` from a boolean, and Vue drops a false attribute: an
    // off switch would have no aria-checked at all. Say "false" out loud.
    syncSwitchAria () {
      var sw = this.$refs.sendMailSwitch

      if (sw && sw.$el && typeof sw.$el.setAttribute === 'function') {
        sw.$el.setAttribute('aria-checked', this.sendMailOff ? 'false' : 'true')
      }
    },

    // Send E-mails: the request's own flag, saved at once (Carlos, 2026-10-18) by the
    // normal per-field save (XOL-596).
    onSendMailChange (on) {
      this.$set(this.actionData.emails, 'sendMail', on)
      this.fieldChanged(this.actionData, 'emails.sendMail')
      this.saveNow(['emails.sendMail'])
    },

    // ---- The typed address -------------------------------------------------

    addUserId (party) {
      return 'emailsetup-adduser-' + party + '-' + this._uid
    },

    addErrorId (party) {
      return 'recipients-add-error-' + this._uid + '-' + party
    },

    clearAddError (party) {
      this.addError[party] = null
    },

    // On blur: say now that a non-empty address is malformed.
    validateTypedOnBlur (party) {
      var v = (this.typed[party] || '').trim()

      if (v !== '' && !isValidEmail(v)) {
        this.addError[party] = this.text('emailSetup.add.error.format')
      }
    },

    // The reason a typed address cannot be added, or null. Format first, then a
    // duplicate in this column's Additional, then an address of one of THIS party's
    // own users (it must be added from the user list, so it keeps following their
    // profile, plan U2).
    typedProblem (col, email) {
      if (!isValidEmail(email)) {
        return this.text('emailSetup.add.error.format')
      }

      var key = recipientKey({ email: email })
      var added = this.listsOf(col.party).additional

      for (var i = 0; i < added.length; i++) {
        if (recipientKey(added[i]) === key) {
          return this.text('emailSetup.add.error.duplicate', { email: email })
        }
      }

      var users = col.model.users || []

      for (var u = 0; u < users.length; u++) {
        if (recipientKey({ email: users[u].email }) === key) {
          return this.text('emailSetup.add.error.isUser', { name: users[u].name })
        }
      }

      return null
    },

    addTyped (col) {
      var party = col.party
      var email = (this.typed[party] || '').trim()

      if (email === '' || !this.canEdit) return

      var problem = this.typedProblem(col, email)

      if (problem) {
        this.addError[party] = problem
        return
      }

      var alerts = col.view.alerts
      var entry = additionalEntry(null, email, alerts.indexOf('upload') !== -1, alerts.indexOf('approval') !== -1)

      this.commitList(party, 'additional', this.listsOf(party).additional.concat([entry]))
      this.typed[party] = ''
      this.addError[party] = null
    },

    // ---- Send Test -----------------------------------------------------------

    // Flush what is on screen first, so the test goes to what the user sees; a failed
    // flush has said why, and nothing is sent. Then the API sends one e-mail per address.
    sendTest (party) {
      var self = this

      if (!this.canEdit || this.testing[party]) return Promise.resolve()

      this.testing[party] = true

      return this.flushBeforeSend([this.actionData]).then(function (ok) {
        return ok ? self.notifyRequest('test', { party: party }) : null
      }).then(function () {
        self.testing[party] = false
      }, function (e) {
        self.testing[party] = false
        throw e
      })
    },

    // ---- What each column says -----------------------------------------------

    summaryParts (col) {
      var self = this
      // One part per alert the column carries (the read model's `alerts`). The provider's
      // approval alert is the file-approved notice, off for now (Carlos, 2026-10-17), so the
      // API sends it Upload only; it comes back here if that trigger is turned on again.
      var map = col.view.alerts.map(function (a) {
        return [a, a === 'approval' && col.party === 'provider' ? 'fileApproved' : a]
      })

      return map.map(function (pair) {
        var n = col.view.counts[pair[0]]

        var text = self.text('emailSetup.summary.' + pair[1] + (n === 0 ? '.none' : ''), { n: n, will: col.will })

        // `lead` is the number (shown bold), `rest` the words after it.
        return {
          key: pair[1],
          none: n === 0,
          text: text,
          lead: n === 0 ? '' : String(n),
          rest: n === 0 ? text : text.substring(String(n).length)
        }
      })
    },

    // The words after the number of "1 with an e-mail problem" (the number itself is bold).
    problemsRest (n) {
      return this.text('emailSetup.summary.problems', { p: n }).substring(String(n).length)
    },

    // ---- The alert cells of a user row ------------------------------------------

    cellText (state) {
      return this.text('emailSetup.cell.' + (state === 'unsubscribed' ? 'off' : state))
    },

    cellTitle (state) {
      return this.text('emailSetup.cell.' + state + '.title', { rtx: this.rtx })
    },

    cellIcon (state) {
      if (state === 'on' || state === 'added') return 'fa fa-check'
      return state === 'unsubscribed' ? 'fa fa-minus-circle' : 'fa fa-minus'
    },

    cellIconColour (state) {
      return state === 'on' || state === 'added' ? 'text-success' : 'placeholder-text'
    },

    // ---- The column header ---------------------------------------------------------

    // What the column's alerts are, in one line. The Tower Co's names the owner, because its
    // approval e-mails are not its own. An RTA (XOL-67) has its own two lines: its client is
    // `client`, not `organisation`, and each column names the other side.
    helpText (col) {
      var org = this.actionData.organisation || (this.isRta ? this.actionData.client : null)

      var key = 'emailSetup.help.' + col.party

      if (this.isRta && (col.party === 'towerco' || col.party === 'owner')) {
        key += '.rta'
      } else if (col.party === 'provider' && col.view.alerts.indexOf('approval') !== -1) {
        key = 'emailSetup.help.provider.fileApproved'
      }

      return this.text(key, { owner: org ? (org.name || org.shortName) : null, towerCo: this.isRta ? this.towerCoName() : null })
    },

    // The Send Test helper (the tooltip on desktop, plain text on a phone). Nothing to test is
    // said instead when the column reaches nobody.
    testTip (col) {
      if (col.view.testCount === 0) {
        return this.text('emailSetup.test.disabledTitle')
      }

      return this.text('emailSetup.test.helper', { n: col.view.testCount }) +
        (this.sendMailOff ? ' ' + this.text('emailSetup.test.whileOff') : '')
    },

    testHelpId (party) {
      return 'recipients-test-help-' + this._uid + '-' + party
    },

    // ---- The Users list: search, toggles, "How to fix" ---------------------------

    userMatches (u, query) {
      if (!query) return true

      return String(u.name || '').toLowerCase().indexOf(query) !== -1 ||
        String(u.email || '').toLowerCase().indexOf(query) !== -1
    },

    queryOf (party) {
      return String(this.userQuery[party] || '').trim()
    },

    // After the query settles (300 ms), tell a screen reader what the lists now hold.
    onQueryInput (col) {
      var self = this
      var party = col.party

      if (this.queryTimers[party]) {
        clearTimeout(this.queryTimers[party])
      }

      this.queryTimers[party] = setTimeout(function () {
        self.queryTimers[party] = null

        var now = self.columns.filter(function (c) { return c.party === party })[0]

        if (!now || !now.query) {
          self.queryStatus[party] = ''
          return
        }

        if (now.noMatches) {
          self.queryStatus[party] = self.text('emailSetup.users.search.none', { org: now.orgName, q: self.queryOf(party) })
          return
        }

        var parts = []

        if (now.recvMatches.length > 0) {
          parts.push(self.text('emailSetup.users.receivingMatch', { m: now.recvMatches.length, n: now.view.groups.receiving.length }))
        }
        if (now.noAddressMatches.length > 0) {
          parts.push(self.text('emailSetup.users.noAddressMatch', { m: now.noAddressMatches.length }))
        }

        self.queryStatus[party] = parts.join('. ')
      }, 300)
    },

    // Why the Users list is empty. Users who get nothing are not listed, so "no users" and
    // "nobody gets e-mails" are different sentences.
    emptyUsersText (col) {
      if (col.view.users.length === 0) {
        return this.text('emailSetup.users.empty', { org: col.orgName })
      }

      if (col.view.hiddenCount === col.view.users.length) {
        return this.text('emailSetup.users.allHidden', { org: col.orgName })
      }

      return this.text('emailSetup.users.noneReceive', { org: col.orgName, rtx: this.rtx })
    },

    listId (party, name) {
      return 'recipients-list-' + this._uid + '-' + party + '-' + name
    },

    toggleNoAddress (party) {
      this.noAddressShown[party] = !this.noAddressShown[party]
    },

    toggleReceivingAll (party) {
      this.receivingAll[party] = !this.receivingAll[party]
    },

    userFixKey (col, u) {
      return col.party + ':u' + u.contact_id
    },

    entryFixKey (col, e) {
      return col.party + ':a' + e.key
    },

    fixId (key) {
      return 'recipients-fix-' + this._uid + '-' + String(key).replace(/[^A-Za-z0-9_-]/g, '_')
    },

    isFixOpen (key) {
      return !!this.fixOpen[key]
    },

    // An address split after "@" and each "." (each piece is followed by a <wbr>), so a narrow
    // column wraps it there instead of mid-word. Copying the text still gives the plain address.
    emailParts (email) {
      return String(email || '').match(/[^@.]+[@.]?|[@.]/g) || []
    },

    toggleFix (key) {
      this.$set(this.fixOpen, key, !this.fixOpen[key])
    },

    // The short problem line of a row: "Invalid e-mail", "Last e-mail failed on 14 Oct 2026".
    shortReason (invalid) {
      return this.text('emailSetup.invalid.short.' + invalid.reason, {
        date: invalid.failed_at ? this.formatDate(invalid.failed_at) : null
      })
    },

    // The bell's words: it turns e-mails back on once the user has an alert that is Off and an Additional
    // entry or an unsubscribe (u.canTurnOn), and unsubscribes them otherwise.
    bellTitle (user) {
      return user.canTurnOn
        ? this.text('emailSetup.user.resubscribe.title', { rtx: this.rtx })
        : this.text('emailSetup.user.unsubscribe.title', { rtx: this.rtx })
    },

    // The slashed bell of a column with two alerts opens the panel (it can turn alerts on as well as off), so it is
    // named for the panel; a single-alert column toggles at once and keeps the action's words. The plain bell
    // already reads "Turn e-mails back on".
    bellAria (col, user) {
      if (user.canTurnOn) {
        return this.text('emailSetup.user.resubscribe.aria', { name: user.name })
      }

      if (this.hasPanel(col)) {
        return this.text('emailSetup.user.panel.aria', { name: user.name, rtx: this.rtx })
      }

      return this.text('emailSetup.user.unsubscribe.aria', { name: user.name, kinds: this.kindsText(col, user) })
    },

    // "Upload", "Approval" or "Upload and Approval", for the Unsubscribe label.
    kindsText (col, user) {
      var self = this

      return col.view.alerts.filter(function (a) { return user.alerts[a].receives }).map(function (a) {
        return self.text('emailSetup.ticks.' + a)
      }).join(' and ')
    },

    whoOf (e) {
      return e.typed ? e.email : (e.name || this.text('emailSetup.entry.missing'))
    },

    // The last tick of a TYPED address stays on: remove it with the (X) instead. An org user's entry may
    // end with every tick off (Delta 5b): it stays listed until the (X).
    isLastTick (entry, alert, alerts) {
      if (entry[alert] !== true || (entry.contact_id !== undefined && entry.contact_id !== null)) {
        return false
      }

      return alerts.every(function (a) { return a === alert || entry[a] !== true })
    },

    invalidReason (invalid, email) {
      if (invalid.reason === 'last_failed') {
        return this.text('emailSetup.invalid.last_failed', {
          date: invalid.failed_at ? this.formatDate(invalid.failed_at) : null,
          reason: invalid.error_code && ['invalid_address', 'transport'].indexOf(invalid.error_code) !== -1
            ? this.text('emailSetup.invalid.reason.' + invalid.error_code)
            : null
        })
      }

      return this.text('emailSetup.invalid.' + invalid.reason, { email: email })
    },

    // Where a user can fix a bad address: their own User Profile, or their
    // organisation's contacts in Settings, for the people allowed to open that tab.
    // Opened in a new tab so the request drawer stays open; there is no deep link to a
    // single contact yet.
    fixLink (col, row, isSelf) {
      if (!row.invalid) {
        return null
      }

      if (isSelf) {
        return { href: '#/profile', text: this.text('emailSetup.invalid.link.self') }
      }

      var slug = SETTINGS_TAB[col.model.organisation ? col.model.organisation.category : null]

      if (slug && this.canOpenContactSettings(col.party)) {
        return { href: '#/settings/' + slug, text: this.text('emailSetup.invalid.link.org', { org: col.orgName }) }
      }

      return null
    },

    canOpenContactSettings (party) {
      return (this.isSuper() || this.isManager('admin|management')) &&
        (party === 'towerco' ? this.isManager() : this.canAccess('project|maintenance|finance|management'))
    },

    // The "Add a user" options: every user of the party, A-Z. One that cannot be added
    // says why instead of showing the e-mail.
    userOptions (col) {
      var self = this
      var added = {}

      col.view.additional.forEach(function (e) { added[e.key] = true })

      // A user with no address (one who would get e-mails, or one who would not) is not offered:
      // adding them could not reach anyone (Delta 1, item 5b). A user who gets nothing here but
      // has an address IS offered: the Users list does not show them, so this is how they are added.
      var offered = col.view.users.filter(function (v) { return v.group === 'receiving' || v.group === 'off' })
      var offeredIds = {}

      offered.forEach(function (v) { offeredIds[Number(v.contact_id)] = true })

      return (col.model.users || []).filter(function (u) {
        return offeredIds[Number(u.contact_id)] === true
      }).map(function (u) {
        var view = null

        col.view.users.forEach(function (v) { if (v.contact_id === u.contact_id) view = v })

        var why = null

        if (added['c:' + Number(u.contact_id)]) {
          why = self.text('emailSetup.add.user.already')
        } else if (view && view.invalid && (view.invalid.reason === 'no_email' || view.invalid.reason === 'bad_format')) {
          why = self.text('emailSetup.add.user.noValid')
        } else if (view && col.view.alerts.every(function (a) { return view.alerts[a].receives })) {
          why = self.text('emailSetup.add.user.getsAll')
        }

        return { contact_id: u.contact_id, name: u.name, email: u.email, why: why }
      }).sort(function (a, b) {
        var x = String(a.name || '').toLowerCase()
        var y = String(b.name || '').toLowerCase()

        return x < y ? -1 : (x > y ? 1 : 0)
      })
    },

    // ---- Focus -----------------------------------------------------------------

    focusEl (selector, el) {
      var self = this

      this.$nextTick(function () {
        var target = el || (selector ? self.$el.querySelector(selector) : null)

        if (target && typeof target.focus === 'function') {
          target.focus()
        }
      })
    },

    // After Remove / Re-subscribe: the next row's same button, else the previous
    // row's, else `fallback` (the Add select, or the section heading).
    focusAfterRemoval (group, fallback, index) {
      var self = this

      this.$nextTick(function () {
        var buttons = self.$el.querySelectorAll('[data-focus="' + group + '"]')
        var target = buttons[index] || buttons[index - 1] || self.$el.querySelector('[data-focus="' + fallback + '"]')

        if (target && fallback.indexOf('add-') === 0 && target.querySelector('input')) {
          target = target.querySelector('input')
        }

        if (target && typeof target.focus === 'function') {
          target.focus()
        }
      })
    }

  },


  watch: {
    // While a panel is open it owns Esc inside a request layer (the FieldCue pattern).
    unsubFor (now) {
      var reg = this.drawerDirtyRegistry

      if (!reg || typeof reg.registerEscClaim !== 'function') {
        return
      }

      if (now) {
        reg.registerEscClaim(this.claimEsc)
      } else {
        reg.unregisterEscClaim(this.claimEsc)
      }
    }
  },


  // The popup's body is rendered on its FIRST open, which is when this mounts: it reads
  // the lists then. Every later open reloads through RequestWorkflow (`reload`).
  mounted () {
    this.syncSwitchAria()
    this.reload()
  },

  updated () {
    this.syncSwitchAria()
  },


  beforeDestroy () {
    // An answer that arrives after the popup is gone is dropped.
    this.loadSeq += 1

    var timers = this.queryTimers

    Object.keys(timers).forEach(function (party) {
      if (timers[party]) clearTimeout(timers[party])
    })

    var reg = this.drawerDirtyRegistry

    if (reg && typeof reg.unregisterEscClaim === 'function') {
      reg.unregisterEscClaim(this.claimEsc)
    }
  }

}

</script>


<style scoped>
/* Delta 1 (item 7): every size here is px. Inside the Popup drawer, text inherits
   `body { font-size: 1.2rem }` and `h4 { font-size: 1.5rem; color: silver }` from app.css
   (about 19px and 24px), which is what made this screen hard to read. app.css is shared by
   the whole app, so the type scale is set here. Spacing uses 4, 6, 8, 12 and 16 only. */
.email-setup { font-size: 13px; line-height: 1.5; color: #606266; }
.email-setup__title { margin: 0 0 12px; }

.visually-hidden { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }

.recipients-toolbar { display: flex; align-items: center; gap: 12px; margin: 12px 0; }
.recipients-toolbar__spacer { flex: 1; }
.recipients-toolbar__mail { display: flex; align-items: center; gap: 8px; min-width: 0; }
.recipients-toolbar__mail-label { font-size: 14px; color: #303133; cursor: pointer; }
.recipients-toolbar__mail-label.is-disabled { color: #909399; cursor: not-allowed; }
/* Element's switch has no focus style: its input is hidden, so a keyboard user would see nothing. */
.recipients-toolbar__switch ::v-deep .el-switch__input:focus-visible ~ .el-switch__core { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #409EFF; }
.recipients-off { margin-bottom: 12px; }
.recipients-error__retry { margin-top: 8px; }

.recipients-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 16px; }
.recipients-grid.is-narrow { grid-template-columns: 1fr; gap: 12px; }

.recipients-col { border: 1px solid #EBEEF5; border-radius: 4px; padding: 12px 16px; min-width: 0; }

/* The column header: name + party | Send Test, then the help, then the summary. */
.recipients-col__head { display: grid; grid-template-columns: 1fr auto; gap: 4px 12px; align-items: center; padding-bottom: 12px; border-bottom: 1px solid #EBEEF5; }
.recipients-col__who { min-width: 0; }
.recipients-col__name { display: inline; margin: 0; font-size: 16px; font-weight: 600; color: #303133; }
.recipients-col__party { margin-left: 6px; vertical-align: 2px; }
.recipients-col__test { text-align: right; }
.recipients-col__test-wrap { display: inline-block; }
/* Element only spaces its own el-icon-* icons from the label; these are Font Awesome (§4 / D5: 5px on the label). */
.recipients-refresh ::v-deep .fa + span { margin-left: 5px; }
.recipients-col__test ::v-deep .el-button > i.fa + span { margin-left: 5px; }
.recipients-group__toggle ::v-deep .fa + span { margin-left: 5px; }
.recipients-group__warn { margin-right: 5px; }
.recipients-add__typed ::v-deep .fa + span { margin-left: 5px; }
.recipient-row__remove { color: #F56C6C; }
.recipients-col__test-help { display: block; font-size: 12px; margin-top: 4px; }
.recipients-col__help { grid-column: 1 / -1; font-size: 12px; color: #909399; }
.recipients-col__summary { grid-column: 1 / -1; font-size: 12px; color: #606266; margin: 0; }
.recipients-col__summary b { color: #303133; }
.recipients-col__problems, .recipients-col__problems b { color: #F56C6C; }
.recipients-col__nobody { grid-column: 1 / -1; margin: 8px 0 0; }
.is-narrow .recipients-col__head { grid-template-columns: 1fr; }
.is-narrow .recipients-col__test { order: 4; text-align: left; }
.is-narrow .recipients-col__test-wrap, .is-narrow .recipients-col__test .el-button { display: block; width: 100%; }

.recipients-section { margin-top: 12px; }
.recipients-section__title { margin: 16px 0 6px; font-size: 13px; font-weight: 600; color: #303133; }
.recipients-section--first .recipients-section__title { margin-top: 6px; }
.recipients-section__help { display: block; font-size: 12px; color: #909399; margin-bottom: 4px; }
.recipients-search { width: 100%; margin: 0 0 6px; }

.recipients-group { margin-top: 4px; }
.recipients-group__title { font-size: 12px; font-weight: 600; color: #606266; margin: 8px 0 2px; }
.recipients-group__toggle, .recipients-group__more { display: block; padding: 4px 0; font-size: 12px; text-align: left; white-space: normal; height: auto; }
.recipients-legend { display: block; font-size: 12px; color: #909399; margin-top: 4px; }

.recipient-list { list-style: none; padding: 0; margin: 0; }

/* One grid for the header row and every user row: name | one cell per alert | action. */
.recipient-grid__head { display: grid; grid-template-columns: minmax(0, 1fr) repeat(var(--alert-cols, 2), 72px) 32px; column-gap: 6px; font-size: 12px; color: #909399; border-bottom: 1px solid #EBEEF5; padding-bottom: 2px; }
.recipient-row--user { display: grid; grid-template-columns: minmax(0, 1fr) repeat(var(--alert-cols, 2), 72px) 32px; column-gap: 6px; align-items: start; padding: 6px 0; border-bottom: 1px solid #EBEEF5; }
.recipient-row__cells { display: contents; }
.recipient-row__who { min-width: 0; display: flex; flex-wrap: wrap; column-gap: 6px; align-items: baseline; }
.recipient-row__name { font-size: 13px; font-weight: 600; color: #303133; }
.recipient-row__sep { color: #C0C4CC; }
.recipient-row__email { font-size: 12px; color: #606266; word-break: break-all; }
.recipient-row__meta { font-size: 12px; color: #909399; }
.recipient-row__hint { flex-basis: 100%; font-size: 12px; color: #606266; }
.recipient-row__none { font-size: 12px; color: #909399; }
.recipient-row__problem { flex-basis: 100%; font-size: 12px; color: #F56C6C; }
.recipient-row__fix { flex-basis: 100%; font-size: 12px; color: #606266; margin-top: 2px; }
.recipient-row__fix-toggle { padding: 0 0 0 4px; font-size: 12px; }
.recipient-cell { font-size: 12px; white-space: nowrap; padding-top: 2px; }
.recipient-cell__label { display: none; }
.recipient-cell__state { color: #303133; }
.recipient-cell__state.is-off { color: #909399; }
.recipient-row__unsub { padding: 0; color: #909399; text-align: center; }
.recipient-row__unsub:hover { color: #F56C6C; }
/* Delta 5: the plain bell turns e-mails back on, so it reads as the way back, not as a danger. */
.recipient-row__unsub.is-unsubscribed { color: #606266; }
.recipient-row__unsub.is-unsubscribed:hover { color: #409EFF; }
.recipient-row--user .unsub-panel { grid-column: 1 / -1; }

/* Toggle A rows: one line, no cells, no action. */
.recipient-list.is-compact { margin-top: 2px; }
.recipient-row--compact { padding: 4px 0; border-bottom: 1px solid #EBEEF5; }
.recipient-row--compact .recipient-row__line { display: flex; flex-wrap: wrap; align-items: baseline; column-gap: 6px; }

/* The grey "for this request only" box: Additional and Unsubscribed. */
.recipients-overrides { background: #F5F7FA; border-radius: 4px; padding: 12px; margin-top: 16px; }
.recipients-overrides__title { font-size: 12px; font-weight: 600; color: #606266; }
.recipients-overrides .recipients-section { margin-top: 8px; }
.recipients-overrides .recipients-section__title { margin-top: 12px; }
.recipients-overrides .recipients-section--first .recipients-section__title { margin-top: 6px; }
.recipient-row--override { display: grid; grid-template-columns: minmax(0, 1fr) auto 32px; column-gap: 8px; align-items: start; background: #fff; border: 1px solid #EBEEF5; border-radius: 4px; padding: 6px 8px; margin-bottom: 4px; }
.is-towerco .recipient-row--override { grid-template-columns: minmax(0, 1fr) 32px; }
.recipient-row--unsub, .is-towerco .recipient-row--unsub { grid-template-columns: minmax(0, 1fr) auto; column-gap: 12px; }
.recipient-row__ticks { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; }
.recipient-row__ticks ::v-deep .el-checkbox__label { font-size: 12px; }
/* The row's gap spaces the boxes (Element's own 30px right margin would wrap them on a phone). */
.recipient-row__ticks ::v-deep .el-checkbox { margin-right: 0; }
/* Additional / Unsubscribed rows stack the name over the e-mail at every width (Carlos,
   2026-10-17): in a three-column popup the pair wrapped separately and left the "·" on a
   line of its own. The name's 19px line keeps it centred on the checkboxes and the ×. */
.recipient-row--override .recipient-row__who { flex-direction: column; align-items: flex-start; }
.recipient-row--override .recipient-row__sep { display: none; }
.recipient-row--override .recipient-row__who > .recipient-row__name { line-height: 19px; }
.recipient-row--override .recipient-row__email, .recipient-row--override .recipient-row__meta { line-height: 16px; word-break: normal; overflow-wrap: anywhere; }

/* Remove (×) and Re-subscribe sit in the row's first line, which is 19px tall: the checkbox
   height, and the name's centre line (Carlos, 2026-10-17: the × sat 2.5px high and
   Re-subscribe 4.5px low). Same box, centred content, so all three line up. */
.recipient-row__remove, .recipient-row__resub { align-self: start; justify-self: end; height: 19px; padding: 0; display: inline-flex; align-items: center; justify-content: center; }
.recipient-row__remove { margin-left: auto; font-size: 14px; }
.recipient-row__resub { font-size: 12px; line-height: 19px; }

.unsub-panel { background: #F5F7FA; padding: 8px 12px; border-radius: 4px; margin-top: 6px; }
.unsub-panel p { margin: 0 0 6px; }
/* [Cancel] [Save] on desktop (Cancel first in the markup); at phone width the buttons stack
   full-width with Save on top, so the primary action is the first thing under the boxes. */
.unsub-panel__buttons { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.unsub-panel__buttons .el-button { margin-left: 0; }
.is-narrow .unsub-panel__buttons { flex-direction: column-reverse; gap: 6px; }
.is-narrow .unsub-panel__buttons .el-button { width: 100%; }

.recipients-add { margin-top: 8px; }
.recipients-add__typed { display: flex; gap: 8px; margin-top: 6px; }
.recipients-add__input { flex: 1; }
.recipients-add__input.is-error >>> .el-input__inner { border-color: #F56C6C; }
.recipients-add__typed .el-button { flex: none; }
.recipients-add__error { font-size: 12px; color: #F56C6C; margin: 4px 0 0; }

.opt-email { float: right; font-size: 12px; color: #8492A6; margin-left: 12px; }

/* Phone: no header row; the cells drop under the name with a visible label; the e-mail wraps
   under the name; the overrides box rows stack. */
.is-narrow .recipient-grid__head { display: none; }
.is-narrow .recipient-row--user { grid-template-columns: minmax(0, 1fr) 32px; }
.is-narrow .recipient-row--user .recipient-row__who { grid-column: 1; grid-row: 1; flex-direction: column; align-items: flex-start; }
.is-narrow .recipient-row--user .recipient-row__sep { display: none; }
.is-narrow .recipient-row--user .recipient-row__unsub { grid-column: 2; grid-row: 1; }
.is-narrow .recipient-row__cells { display: flex; flex-wrap: wrap; gap: 4px 12px; grid-column: 1 / -1; }
.is-narrow .recipient-cell__label { display: inline; margin-right: 4px; color: #606266; }
.is-narrow .recipient-row--override { grid-template-columns: minmax(0, 1fr) auto; }
.is-narrow .recipient-row--override .recipient-row__who { grid-column: 1 / -1; flex-direction: column; align-items: flex-start; }
.is-narrow .recipient-row--override .recipient-row__sep { display: none; }
.is-narrow .is-towerco .recipient-row--override .recipient-row__who { grid-column: 1; }
.is-narrow .recipient-row--unsub .recipient-row__resub { justify-self: end; }
</style>
